/**
 * Adapter that renders a single-trace backend `PlotData` into WebGLChart.
 *
 * Used for original / slope / processed / STL decomposition / LSTM error
 * plots. Multi-trace plots (anomaly overlays — line + scatter) go through
 * `AnomalyOverlayChart` which uses `MultiSeriesWebGLChart`. If a multi-trace
 * plot accidentally lands here, only the first trace is rendered and a dev
 * warning fires so the regression is visible.
 */
import React, { useMemo } from 'react';

import { WebGLChart } from '@/shared/components/charts/WebGLChart';
import { PlotData } from '../types';

interface Props {
  plot: PlotData;
  /** Optional title override; defaults to plot.title. */
  title?: string;
  /** Series legend label; falls back to first trace label or yaxis label. */
  seriesName?: string;
  /** Tightens the layout for sub-plots inside a card. */
  hideControls?: boolean;
  /** Linked-zoom range shared with sibling charts. Null = no shared range (each chart uses its own bounds). */
  sharedBrushRange?: { startTime: number; endTime: number } | null;
  /** Callback fired when the user zooms/pans this chart. Pass-through to siblings via parent state. */
  onSharedBrushChange?: (range: { startTime: number; endTime: number } | null) => void;
  /** Line color as hex (e.g. "#14B8A6"); defaults to teal when omitted. */
  color?: string;
  /** Total chart height in px; defaults to WebGLChart's CHART_HEIGHT when omitted. */
  height?: number;
  /** Hide the "Show points" toggle while keeping Reset Zoom + point count. */
  hidePointsToggle?: boolean;
  /** Suppress the automatic <h5> heading so the caller can render a custom one. */
  hideTitle?: boolean;
  /** Optional React node rendered in place of the default <h5> heading. */
  headerContent?: React.ReactNode;
}

const AnomalyChartImpl: React.FC<Props> = ({
  plot,
  title,
  seriesName,
  hideControls,
  sharedBrushRange,
  onSharedBrushChange,
  color,
  height,
  hidePointsToggle,
  hideTitle,
  headerContent,
}) => {
  if (process.env.NODE_ENV !== 'production' && (plot.traces?.length ?? 0) > 1) {
    console.warn(
      '[AnomalyChart] plot has %d traces; only the first is rendered. ' +
        'Use a multi-series renderer for anomaly overlays.',
      plot.traces.length,
    );
  }

  const label = seriesName ?? plot.traces?.[0]?.label ?? plot.yaxis.label;
  const displayTitle = title ?? plot.title;

  // WebGLChart reads y-values from a property whose key matches `seriesName`;
  // emit the data using `label` as the dynamic key so the chart sees real
  // values instead of always 0/1.
  const data = useMemo(() => {
    const trace = plot.traces?.[0];
    if (!trace) return [];
    const len = Math.min(trace.x.length, trace.y.length);
    const out: Array<Record<string, unknown> & { UTC: string | number }> = [];
    for (let i = 0; i < len; i++) {
      const x = trace.x[i];
      const y = trace.y[i];
      if (x == null) continue;
      out.push({ UTC: x, [label]: y ?? null });
    }
    return out;
  }, [plot, label]);

  if (data.length === 0) {
    return (
      <div className="rounded border border-border bg-muted p-4 text-sm text-muted-foreground">
        {displayTitle}: no data
      </div>
    );
  }

  // When a custom height is supplied the caller is in a compact context
  // (e.g. STL decomposition grid where colors differentiate charts) — drop
  // the per-chart title entirely and tighten wrapper padding to maximize
  // the visible plot area.
  const compact = height !== undefined;
  const wrapperCls = compact
    ? 'rounded border border-border bg-card p-2'
    : 'rounded border border-border bg-card p-3';

  return (
    <div className={wrapperCls}>
      {!compact && (headerContent ?? (!hideTitle && (
        <h5 className="mb-2 text-sm font-medium text-foreground">{displayTitle}</h5>
      )))}
      <WebGLChart
        data={data}
        seriesName={label}
        hideControls={hideControls}
        sharedBrushRange={sharedBrushRange}
        onSharedBrushChange={onSharedBrushChange}
        color={color}
        height={height}
        hidePointsToggle={hidePointsToggle}
      />
    </div>
  );
};

const AnomalyChart = React.memo(AnomalyChartImpl);
export default AnomalyChart;
